import { cloudLayerLimits, CloudLayerLimitError } from '../shared/cloud-layer-limits.js';
import { dataLayerContentStamp } from '../shared/data-layer-snapshot.js';
import { datasetBatches } from './local-dataset-store.js';
import { dataLayerPayload, validateDataLayerPayload, type DataLayer } from '../shared/data-layer.js';
import { jsonByteLength } from '../shared/json-size.js';
import { encodeImportSource } from './import-source-files.js';

/** Remote payloads must contain source bytes, never browser-local handles. */
export async function portableDataLayerPayload(layer: DataLayer, pro = false) {
  const { maxFeatures, maxBytes } = cloudLayerLimits(pro);
  const payload = dataLayerPayload(layer);
  if (layer.dataset) {
    if (layer.dataset.count > maxFeatures) throw new CloudLayerLimitError('features');
    payload.features = [];
    let bytes = jsonByteLength(payload);
    for await (const batch of datasetBatches(layer.dataset)) {
      bytes += jsonByteLength(batch);
      if (bytes > maxBytes || payload.features.length + batch.length > maxFeatures) throw new CloudLayerLimitError(bytes > maxBytes ? 'bytes' : 'features');
      payload.features.push(...batch);
    }
    if (payload.features.length !== layer.dataset.count) throw new Error('The local layer is incomplete. Keep this tab open and recover the original file before saving.');
    payload.layer.featureCount = payload.features.length;
  }
  const sources = layer.retainedSources ?? [];
  const sourceBytes = sources.reduce((total, source) => total + 4 * Math.ceil(source.data.size / 3), 0);
  checkSourceLimits(layer, jsonByteLength(payload, maxBytes) + sourceBytes, maxFeatures, maxBytes);
  if (sources.length) {
    payload.layer.sourceFiles = [...payload.layer.sourceFiles ?? []];
    for (const source of sources) payload.layer.sourceFiles.push(await encodeImportSource(new File([source.data], source.name)));
  }
  const result = validateDataLayerPayload(payload, { maxFeatures, maxBytes });
  if (!result.success && result.code === 'payload_limit') throw new CloudLayerLimitError('bytes');
  if (!result.success) throw new Error(result.errors.join(' '));
  return result.payload;
}

/** Prepare complete records before synchronous live sharing can see a local dataset. */
export async function materializeCloudLayers(layers: DataLayer[], pro = false): Promise<void> {
  const pending = layers.filter(layer => layer.dataset).map(layer => ({ layer, stamp: dataLayerContentStamp(layer) }));
  const payloads = await Promise.all(pending.map(({ layer }) => portableDataLayerPayload(layer, pro)));
  if (pending.some(({ layer, stamp }) => dataLayerContentStamp(layer) !== stamp)) throw new Error('The layer changed while preparing the save. Try again.');
  pending.forEach(({ layer }, index) => {
    layer.features = payloads[index].features;
    layer.featureCount = layer.features.length;
    delete layer.dataset;
  });
}

function checkSourceLimits(layer: DataLayer, bytes: number, maxFeatures: number, maxBytes: number): void {
  if (layer.features.length > maxFeatures) throw new CloudLayerLimitError('features');
  if ((layer.retainedSources ?? []).some(source => 4 * Math.ceil(source.data.size / 3) > 20 * 1024 * 1024)) throw new CloudLayerLimitError('source_file');
  if (bytes > maxBytes) throw new CloudLayerLimitError('bytes');
}
